import React, { useState, useMemo, useEffect } from "react";
import { connect } from "react-redux";

import { bindActionCreators } from "redux";

import { FilterSection, FilterItem, FilterItemField, PickerInput, DataColumnDef, RequiredState } from "@plex/react-components";

import { IKeyData } from "infrastructure";
import { arrayHelpers } from "infrastructure/helpers";
import { useLocalizedValues, useLocalizerWithFallback, useUserContext } from "ui/hooks";
import {
  IAccountsList,
  ITenantsList,
  ITenant,
  IAccountSearchCriterion,
  IAccount,
  ITenantsState,
  IAccountsState,
  accountActions,
  tenantActions,
  ITenantSearchCriterion
} from "ui/screens/security/models";
import { securityMessages } from "ui/screens/security/security.localization";

import { securityRolesActions } from "../SecurityRoles.actions";
import { ISecurityRolesFilter, ISecurityRolesState } from "../SecurityRoles.state";

export interface ISecurityRolesFilterProps {
  tenants?: ITenantsList;
  accounts?: IAccountsList;
  filter?: ISecurityRolesFilter;
  applyFilter: (filter: ISecurityRolesFilter) => void;
  loadTenants: (request: IKeyData<ITenantSearchCriterion>) => void;
  loadAccounts: (criterion: IKeyData<IAccountSearchCriterion>) => void;
  clearAccounts: (criterion: IKeyData) => void;
}

const tenantPicker = "tenant-picker";
const accountPicker = "account-picker";

export const SecurityRolesFilter: React.FC<ISecurityRolesFilterProps> = ({ applyFilter, filter, tenants, loadTenants, accounts, loadAccounts, clearAccounts }) => {
  const localizedValues = useLocalizedValues(() => ({
    tenant: securityMessages.filter("tenant"),
    account: securityMessages.filter("account")
  }));

  const localizeWithFallback = useLocalizerWithFallback();
  const userContext = useUserContext();

  const [state, setState] = useState<{
    criteria: ISecurityRolesFilter;
    pinned: boolean;
  }>({
    criteria: {},
    pinned: false
  });

  const onPickerSelect = <T, P extends keyof ISecurityRolesFilter>(rows: T[], name: P) => {
    const selected = rows[0];

    if (name === "tenant") {
      setState(prev => ({
        ...prev,
        criteria: { ...prev.criteria, [name]: selected, account: undefined }
      }));

      clearAccounts({ id: accountPicker } as IKeyData);
    } else {
      setState(prev => ({
        ...prev,
        criteria: { ...prev.criteria, [name]: selected }
      }));
    }
  };

  const selectedTenant = useMemo(() => arrayHelpers.createFrom(state.criteria.tenant), [state.criteria.tenant]);
  const selectedAccount = useMemo(() => arrayHelpers.createFrom(state.criteria.account), [state.criteria.account]);

  useEffect(() => {
    setState(prev => ({
      ...prev,
      criteria: { ...filter }
    }));
  }, [filter]);

  return (
    <FilterSection
      onSearch={() => {
        applyFilter({ ...state.criteria });
      }}
      pinned={state.pinned}
      onPin={pinned => {
        setState(prevState => ({ ...prevState, pinned }));
      }}
    >
      <FilterItem id={tenantPicker} labelText={localizedValues.tenant} required={RequiredState.required}>
        <FilterItemField value={state.criteria.tenant}>
          <PickerInput
            dialogTitle={localizedValues.tenant}
            selected={selectedTenant}
            errorMessage={localizeWithFallback(tenants?.notification?.content[0]?.message)}
            onSelectionChanged={rows => onPickerSelect(rows, "tenant")}
            keySelector={(row: ITenant) => row.tenantId}
            displaySelector={(row: ITenant) => row.tenantName}
            data={tenants?.items}
            onSearch={(query, cols) =>
              loadTenants({
                id: tenantPicker,
                data: {
                  enterpriseId: userContext.getTenants().find(e => e.tenantId === userContext.getTenantId())?.enterpriseId,
                  query,
                  searchedColumns: cols
                }
              })
            }
          >
            <DataColumnDef<ITenant> id="tenantName" title={localizedValues.tenant} valueSelector={row => row.tenantName} searchable>
              {row => row.tenantName}
            </DataColumnDef>
          </PickerInput>
        </FilterItemField>
      </FilterItem>
      <FilterItem id={accountPicker} labelText={localizedValues.account}>
        <FilterItemField value={state.criteria.account}>
          <PickerInput
            dialogTitle={localizedValues.account}
            selected={selectedAccount}
            errorMessage={localizeWithFallback(accounts?.notification?.content[0]?.message)}
            onSelectionChanged={rows => onPickerSelect(rows, "account")}
            keySelector={(row: IAccount) => row.accountId}
            displaySelector={(row: IAccount) => row.accountName}
            disabled={state.criteria.tenant == null}
            data={accounts?.items}
            onSearch={(query, cols) => loadAccounts({ id: accountPicker, data: { tenantId: state.criteria.tenant?.tenantId, query: query, searchedColumns: cols } })}
          >
            <DataColumnDef<IAccount> id="accountName" title={localizedValues.account} valueSelector={row => row.accountName} searchable>
              {row => row.accountName}
            </DataColumnDef>
          </PickerInput>
        </FilterItemField>
      </FilterItem>
    </FilterSection>
  );
};

export default connect(
  (state: { securityRoles: ISecurityRolesState; tenants: ITenantsState; accounts: IAccountsState }) => {
    return {
      tenants: state.tenants[tenantPicker],
      accounts: state.accounts[accountPicker],
      filter: state.securityRoles.filter
    };
  },
  dispatch => {
    return bindActionCreators(
      {
        applyFilter: securityRolesActions.applyFilter,
        loadAccounts: accountActions.loadAccounts,
        loadTenants: tenantActions.loadTenants,
        clearAccounts: accountActions.clearAccounts
      },
      dispatch
    );
  }
)(SecurityRolesFilter);
